import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import { listingImageUrl } from "@/lib/productGallery";
import { useStoreBase } from "@/lib/store";
import { useCartUI } from "@/lib/cartui";
import { useWishlist } from "@/lib/wishlist";
import { Star, Heart, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { money } from "@/lib/money";
import { storeOrdersPaused, storeOfflineMessage } from "@/lib/timeslotCheckout";
import { productHasVariants } from "@/lib/productVariants";
import { useListingAddToCart } from "./VariantSelectModal";

export function Stars({ value = 0, size = "sm" }) {
  const cls = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        const fill = value >= i ? 1 : value >= i - 0.5 ? 0.5 : 0;
        return (
          <span key={i} className="relative inline-block">
            <Star className={`${cls} text-stone-300`} />
            {fill > 0 && (
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <Star className={`${cls} text-amber-400`} fill="#fbbf24" />
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

function productColorDots(p) {
  const out = [];
  const seen = new Set();
  (p?.variants || []).forEach((v) => {
    const hex = String(v?.attributes?.color_hex || "").trim();
    if (hex && !seen.has(hex)) {
      seen.add(hex);
      out.push(hex);
    }
  });
  return out.slice(0, 5);
}

function OverlayNote({ paused, pausedMsg, outOfStock, testId, dark = false }) {
  if (!paused && !outOfStock) return null;
  return (
    <div className={`absolute inset-0 ${dark ? "bg-black/50" : "bg-white/50"} flex items-end justify-center p-2`}>
      <span className={`px-2 py-1 ${dark ? "bg-black/80" : "bg-stone-900/85"} text-white text-[10px] font-semibold leading-snug text-center`} data-testid={paused ? testId : undefined}>
        {paused ? pausedMsg : "Out of stock"}
      </span>
    </div>
  );
}

export default function ProductCard(props) {
  const { addFromListing, variantModal } = useListingAddToCart();
  return (
    <>
      <ProductCardBody {...props} addFromListing={addFromListing} />
      {variantModal}
    </>
  );
}

function ProductCardBody({ p, slug, variant = "default", rank, addFromListing }) {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { base, storeConfig } = useStoreBase();
  const paused = storeOrdersPaused(storeConfig);
  const pausedMsg = storeOfflineMessage(storeConfig);
  const { skipCart } = useCartUI();
  const wishlist = useWishlist();
  const cover = listingImageUrl(p.images);
  const discount = p.mrp && p.mrp > p.price ? Math.round(((p.mrp - p.price) / p.mrp) * 100) : 0;
  const rating = Number(p.rating_avg || 0);
  const ratingCount = Number(p.rating_count || 0);
  const saved = wishlist?.has(p.id);
  const outOfStock = p.stock !== null && p.stock !== undefined && p.stock <= 0;
  const needsVariant = productHasVariants(p);
  const addLabel = needsVariant ? "Select options" : (skipCart ? t("store.buyNow") : t("store.addToCart"));

  const [qty, setQty] = useState(1);
  const handleAdd = (e, addQty = 1) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock || paused) return;
    if (p.custom_form?.enabled) {
      nav(`${base}/product/${p.slug}`);
      return;
    }
    addFromListing(p, addQty);
  };

  const handleWish = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const added = wishlist?.toggle(p);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
  };

  if (variant === "boxy") {
    return (
      <Link to={`${base}/product/${p.slug}`} className="group flex flex-col bg-transparent text-left" data-testid={`product-card-${p.slug}`}>
        <div className="relative aspect-[3/4] bg-[#ece8df] overflow-hidden">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {discount > 0 ? (
            <div className="absolute top-2.5 left-2.5 px-2 py-0.5 text-[10px] font-semibold tracking-wide bg-white/90 text-[color:var(--store-primary)]" data-testid={`sale-tag-${p.slug}`}>{discount}% OFF</div>
          ) : null}
          <button type="button" onClick={handleWish} aria-label="Toggle wishlist" data-testid={`wish-btn-${p.slug}`}
            className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center">
            <Heart className="w-4 h-4 text-[#C9A24A]" strokeWidth={1.5} fill={saved ? "#C9A24A" : "none"} />
          </button>
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </div>
        <div className="pt-3 pb-3 border-b border-stone-200">
          <div className="font-display text-sm text-[color:var(--store-primary)] line-clamp-1 leading-snug">{p.name}</div>
          <div className="mt-1 flex items-center flex-wrap gap-1.5">
            <span className="text-sm font-semibold text-stone-900 font-mono-nums">{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price && (
              <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "perfecto") {
    const badge = p.is_featured ? "Best Seller" : (discount > 0 ? `${discount}% OFF` : null);
    return (
      <Link to={`${base}/product/${p.slug}`} className="group flex flex-col bg-white text-left" data-testid={`product-card-${p.slug}`}>
        <div className="relative aspect-square bg-[#f3f1ed] overflow-hidden rounded-sm">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {badge ? (
            <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide bg-[#e8d9c4] text-[#3E2723]" data-testid={`sale-tag-${p.slug}`}>{badge}</div>
          ) : null}
          <button type="button" onClick={handleWish} aria-label="Toggle wishlist" data-testid={`wish-btn-${p.slug}`}
            className="absolute top-2.5 right-2.5 w-8 h-8 flex items-center justify-center text-[#3E2723] hover:scale-110 transition-transform">
            <Heart className="w-4 h-4" fill={saved ? "#3E2723" : "none"} />
          </button>
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </div>
        <div className="pt-3 flex flex-col flex-1">
          {(ratingCount > 0 || rating > 0) && (
            <div className="flex items-center gap-1.5" data-testid={`rating-${p.slug}`}>
              <Stars value={rating || 5} />
              <span className="text-[11px] text-stone-500 font-mono-nums">{(rating || 5).toFixed(1)}</span>
            </div>
          )}
          <div className="mt-1.5 flex items-center flex-wrap gap-1.5">
            <span className="font-mono-nums font-bold text-stone-900">{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price && (
              <>
                <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span>
                <span className="text-[10px] font-semibold text-[#6b4a32] bg-[#f0e6d8] px-1.5 py-0.5 rounded-full">{discount}% OFF</span>
              </>
            )}
          </div>
          <div className="mt-1 text-sm text-stone-800 line-clamp-2 leading-snug">{p.name}</div>
          {p.sku ? <div className="mt-0.5 text-[11px] text-stone-400">{p.sku}</div> : null}
        </div>
      </Link>
    );
  }

  if (variant === "medico") {
    const badgeBits = [];
    if (p.is_featured) badgeBits.push("Hot Seller");
    else if (discount > 0) badgeBits.push("Lowest Price");
    if (ratingCount > 0 || rating > 0) badgeBits.push(`${(rating || 5).toFixed(2)} ★`);
    return (
      <div className="group flex flex-col bg-white rounded-2xl overflow-hidden text-left h-full" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square bg-[#f7f5f2] overflow-hidden">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {badgeBits.length ? (
            <div className="absolute left-2.5 bottom-2.5 px-2 py-0.5 rounded-md text-[10px] font-semibold text-white bg-[#C45C26]" data-testid={`sale-tag-${p.slug}`}>{badgeBits.join(" | ")}</div>
          ) : null}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="pt-3 pb-1 px-1 flex flex-col flex-1">
          <Link to={`${base}/product/${p.slug}`} className="font-bold text-stone-900 text-sm line-clamp-1 leading-snug">{p.name}</Link>
          {p.short_description ? <div className="mt-1 text-xs text-stone-500 line-clamp-1">{p.short_description}</div> : null}
          {p.sku ? <div className="mt-0.5 text-[11px] text-stone-400">{p.sku}</div> : null}
          <div className="mt-2 flex items-baseline justify-between gap-2">
            {p.mrp && p.mrp > p.price ? (
              <span className="text-xs text-stone-400">MRP: <span className="line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span></span>
            ) : <span />}
            <span className="text-sm font-bold text-stone-900 font-mono-nums">Now {money(p.price, p.currency_symbol || "₹")}</span>
          </div>
          <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
            className="mt-3 w-full h-10 rounded-lg text-xs font-bold uppercase tracking-[0.12em] text-white disabled:opacity-50"
            style={{ background: "var(--store-primary, #0F766E)" }}>
            {addLabel}
          </button>
        </div>
      </div>
    );
  }

  if (variant === "cosmos") {
    const chip = (p.short_description || "").replace(/<[^>]+>/g, " ").trim().split(/[.•|]/)[0].trim().slice(0, 28);
    const unit = (p.sku || p.unit || "").toString().slice(0, 18);
    return (
      <div className="group flex flex-col text-left h-full bg-white rounded-2xl border border-stone-200 overflow-hidden" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square bg-[#F7F8FA] overflow-hidden border-b border-stone-200">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {storeConfig?.cosmos_design_enhancer_active && p.cosmos_design?.card_badge?.text ? (
            <span className="absolute left-2 top-2 z-[1] px-2.5 py-0.5 rounded-full text-[11px] font-semibold shadow-sm" style={{ background: p.cosmos_design.card_badge.bg_color || "#16A34A", color: p.cosmos_design.card_badge.text_color || "#FFFFFF" }} data-testid="cosmos-card-badge">{p.cosmos_design.card_badge.text}</span>
          ) : p.is_featured ? (
            <span className="absolute left-2 top-2 px-2 py-0.5 rounded-md text-[10px] font-bold text-white bg-emerald-600" data-testid="cosmos-popular-badge">{(storeConfig?.cosmos_design_enhancer_active && String(storeConfig?.homepage?.cosmos_popular_badge || "").trim()) || "Most Reordered"}</span>
          ) : discount > 0 ? (
            <span className="absolute left-2 top-2 px-2 py-0.5 rounded-md text-[10px] font-bold text-white" style={{ background: "var(--store-primary, #2563EB)" }} data-testid={`sale-tag-${p.slug}`}>{discount}% OFF</span>
          ) : null}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="p-3 flex flex-col flex-1">
          {unit ? <div className="text-[11px] text-stone-400">{unit}</div> : null}
          {chip ? <div className="mt-1 inline-flex self-start items-center rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">{chip}</div> : null}
          <Link to={`${base}/product/${p.slug}`} className="mt-1.5 font-bold text-stone-900 text-sm line-clamp-2 min-h-[2.5rem] leading-snug">{p.name}</Link>
          <div className="mt-1 flex items-center gap-1">
            <Stars value={rating || 0} />
            <span className="text-[11px] text-stone-400 font-mono-nums">{rating ? rating.toFixed(1) : "—"}{ratingCount ? ` (${ratingCount}+)` : ""}</span>
          </div>
          <div className="mt-1.5 flex items-baseline gap-1.5">
            <span className="text-sm font-extrabold text-stone-900 font-mono-nums">{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span> : null}
          </div>
          <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
            className="mt-3 w-full h-10 rounded-lg text-sm font-bold text-white disabled:opacity-50" style={{ background: "var(--store-primary, #2563EB)" }}>
            {addLabel === t("store.addToCart") ? "Add To Cart" : addLabel}
          </button>
        </div>
      </div>
    );
  }

  if (variant === "mixy") {
    const cat = p.category_name || p.category || "";
    return (
      <div className="group flex flex-col text-left h-full" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square rounded-2xl overflow-hidden bg-[#F3F4F6]">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {discount > 0 ? (
            <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-md text-[11px] font-bold text-stone-900" style={{ background: "var(--mixy-accent, #FFDC2E)" }} data-testid={`sale-tag-${p.slug}`}>- {discount}%</div>
          ) : null}
          {p.is_featured ? (
            <span className="absolute left-2.5 bottom-2.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-blue-500">Best Seller</span>
          ) : discount > 20 ? (
            <span className="absolute left-2.5 bottom-2.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-red-500 inline-flex items-center gap-1">Mega Sale</span>
          ) : discount > 0 ? (
            <span className="absolute left-2.5 bottom-2.5 px-2 py-0.5 rounded-full text-[10px] font-bold text-white bg-emerald-500">New</span>
          ) : null}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="pt-3 flex flex-col flex-1">
          {cat ? <div className="text-xs text-stone-400">{cat}</div> : null}
          <Link to={`${base}/product/${p.slug}`} className="mt-0.5 font-bold text-stone-900 text-sm line-clamp-1 leading-snug">{p.name}</Link>
          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="text-sm font-extrabold text-red-600 font-mono-nums">{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? (
              <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span>
            ) : null}
          </div>
          <div className={`mt-1.5 text-xs font-medium ${outOfStock ? "text-stone-400" : "text-emerald-600"}`}>
            {outOfStock ? "Out of stock" : "● In Stock"}
          </div>
          <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
            className="mt-3 w-full h-10 rounded-lg text-sm font-bold text-white bg-stone-900 hover:bg-stone-800 disabled:opacity-50">
            {addLabel}
          </button>
        </div>
      </div>
    );
  }

  if (variant === "various") {
    const cat = p.category_name || p.category || "";
    return (
      <div className="group flex flex-col text-left h-full bg-white rounded-2xl border border-stone-100 shadow-[0_8px_24px_-16px_rgba(0,0,0,0.35)] p-3" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-[4/3] rounded-xl overflow-hidden bg-[#F4F5EE]">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {p.is_featured ? (
            <span className="absolute left-2 top-2 px-2 py-0.5 rounded-md text-[10px] font-bold text-white bg-violet-500">Highly rated</span>
          ) : discount > 0 ? (
            <span className="absolute left-2 top-2 px-2 py-0.5 rounded-md text-[10px] font-bold text-white bg-orange-400">Best Buy</span>
          ) : null}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="pt-2.5 flex flex-col flex-1">
          {cat ? <div className="text-[11px] text-stone-400 truncate">{cat}</div> : null}
          <Link to={`${base}/product/${p.slug}`} className="mt-0.5 font-bold text-stone-900 text-sm line-clamp-2 leading-snug min-h-[2.5rem]">{p.name}</Link>
          <div className="mt-1 flex items-center gap-1">
            <Stars value={rating || 0} />
            <span className="text-xs text-stone-400">({ratingCount})</span>
          </div>
          <div className="mt-1.5 flex items-center flex-wrap gap-1.5">
            <span className="text-base font-extrabold text-red-600 font-mono-nums">{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? (
              <>
                <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span>
                <span className="text-[10px] font-bold text-white bg-red-500 px-1 py-0.5 rounded">-{discount}%</span>
              </>
            ) : null}
          </div>
          <div className={`mt-1 text-xs font-medium inline-flex items-center gap-1 ${outOfStock ? "text-stone-400" : "text-sky-700"}`}>
            {outOfStock ? "Out of stock" : "● In stock"}
          </div>
          <div className="mt-auto pt-3 flex flex-col gap-2">
            <div className="inline-flex items-center h-9 rounded-lg border border-stone-200 bg-white">
              <button type="button" className="w-9 h-full text-stone-600" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQty((q) => Math.max(1, q - 1)); }} aria-label="Decrease">−</button>
              <span className="flex-1 text-center text-sm font-semibold">{qty}</span>
              <button type="button" className="w-9 h-full text-stone-600" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQty((q) => q + 1); }} aria-label="Increase">+</button>
            </div>
            <button type="button" onClick={(e) => handleAdd(e, qty)} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
              className="w-full h-10 rounded-lg text-sm font-bold text-white disabled:opacity-50" style={{ background: "var(--store-primary, #1B4332)" }}>
              {needsVariant ? "Select options" : (skipCart ? t("store.buyNow") : "+ Add")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "locally") {
    const organic = /organic/i.test(`${p.name || ""} ${p.category_name || p.category || ""}`);
    const stockNum = p.stock == null ? null : Number(p.stock);
    const stockMax = stockNum != null && Number.isFinite(stockNum) ? Math.max(stockNum, 40) : null;
    const stockPct = stockNum != null && stockMax ? Math.max(8, Math.min(100, (stockNum / stockMax) * 100)) : null;
    return (
      <div className="group flex flex-col text-left h-full bg-white rounded-2xl border border-stone-100 shadow-[0_10px_28px_-18px_rgba(0,0,0,0.35)] p-3" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square rounded-xl overflow-hidden bg-[#F7F8F4]">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-3 group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {discount > 0 ? (
            <span className="absolute left-2.5 top-2.5 min-w-[2.1rem] h-8 px-1.5 rounded-lg bg-[#F43F7A] text-white text-[11px] font-bold flex items-center justify-center" data-testid={`sale-tag-${p.slug}`}>{discount}%</span>
          ) : null}
          {organic ? (
            <span className="absolute left-2.5 top-12 px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide bg-emerald-50 text-emerald-700 border border-emerald-100">Organic</span>
          ) : null}
          <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
            className="absolute right-2.5 bottom-2.5 w-9 h-9 rounded-full text-white flex items-center justify-center shadow disabled:opacity-50" style={{ background: "var(--store-primary, #0F5C56)" }} aria-label={t("store.addToCart")}>
            +
          </button>
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="pt-3 flex flex-col flex-1">
          <Link to={`${base}/product/${p.slug}`} className="font-bold text-stone-900 text-sm line-clamp-2 leading-snug min-h-[2.5rem]">{p.name}</Link>
          <div className="mt-1.5 flex items-center gap-1">
            <Stars value={rating || 0} />
            <span className="text-xs text-stone-400">({rating ? rating.toFixed(2) : "0"} / {ratingCount || 0})</span>
          </div>
          <div className="mt-1.5 flex items-baseline gap-2">
            <span className="text-base font-extrabold font-mono-nums" style={{ color: "var(--store-primary, #0F5C56)" }}>{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? (
              <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span>
            ) : null}
          </div>
          {stockPct != null ? (
            <div className="mt-3">
              <div className="flex items-center justify-between text-[11px] text-stone-500 mb-1">
                <span>Quantity Left</span>
                <span className="font-bold text-[#F43F7A]">{stockNum}</span>
              </div>
              <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
                <div className="h-full rounded-full bg-[#F43F7A]" style={{ width: `${stockPct}%` }} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (variant === "beast") {
    return (
      <div className="group flex flex-col text-center h-full bg-black" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-[4/5] overflow-hidden bg-[#1a1a1a]">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-6 group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-neutral-600 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {discount > 0 ? (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-sm text-[11px] font-bold text-white bg-red-600" data-testid={`sale-tag-${p.slug}`}>-{discount}%</span>
          ) : p.is_featured ? (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-sm text-[11px] font-bold text-white bg-teal-500">Featured</span>
          ) : null}
          <button type="button" onClick={handleWish} aria-label="Toggle wishlist" data-testid={`wish-btn-${p.slug}`}
            className="absolute bottom-3 right-3 w-8 h-8 rounded-full border border-white/40 flex items-center justify-center text-white">
            <Heart className="w-4 h-4" fill={saved ? "#ffffff" : "none"} />
          </button>
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} dark />
        </Link>
        <div className="pt-4 pb-1 px-1 flex flex-col flex-1 bg-black">
          <Link to={`${base}/product/${p.slug}`} className="font-bold text-white text-sm sm:text-base line-clamp-1">{p.name}</Link>
          <div className="mt-2 flex justify-center">
            <Stars value={rating || 5} />
          </div>
          <div className="mt-2 flex items-center justify-center gap-2 font-mono-nums">
            <span className={`font-bold ${p.mrp && p.mrp > p.price ? "text-orange-500" : "text-white"}`}>{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? (
              <span className="text-sm text-neutral-500 line-through">{money(p.mrp, p.currency_symbol || "₹")}</span>
            ) : null}
          </div>
          <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
            className="mt-4 w-full h-11 rounded-full text-sm font-semibold text-white disabled:opacity-50" style={{ background: "#222222" }}>
            {addLabel}
          </button>
        </div>
      </div>
    );
  }

  if (variant === "qstoRow") {
    return (
      <Link to={`${base}/product/${p.slug}`} className="group flex items-center gap-3 sm:gap-4 text-left h-full bg-white rounded-xl border border-stone-100 p-3 hover:shadow-sm" data-testid={`product-card-${p.slug}`}>
        <div className="relative w-[4.5rem] h-[4.5rem] sm:w-24 sm:h-24 shrink-0 rounded-lg overflow-hidden bg-[#F7F8FA]">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-1.5" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-2xl">{p.name.slice(0, 1)}</div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-stone-900 text-sm line-clamp-2 leading-snug">{p.name}</div>
          {p.mrp && p.mrp > p.price ? (
            <div className="mt-1 text-xs text-stone-400 font-mono-nums">MRP {money(p.mrp, p.currency_symbol || "₹")}</div>
          ) : null}
          <div className="mt-0.5 flex items-center flex-wrap gap-1.5">
            <span className={`text-sm font-extrabold font-mono-nums ${p.mrp && p.mrp > p.price ? "text-red-600" : "text-stone-900"}`}>{money(p.price, p.currency_symbol || "₹")}</span>
            {discount > 0 ? <span className="text-xs font-bold text-red-600">({discount}%)</span> : null}
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "bigshow") {
    const colors = productColorDots(p);
    const limited = Boolean(p.is_featured) && discount <= 0;
    return (
      <div className="group flex flex-col text-left h-full" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square rounded-2xl overflow-hidden bg-[#EEF0F3]">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {rank ? (
            <span className="absolute right-3 top-2 text-7xl font-black leading-none text-stone-200 select-none pointer-events-none" data-testid={`bigshow-rank-${rank}`}>{rank}</span>
          ) : null}
          {discount > 0 ? (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">-{discount}%</span>
          ) : limited ? (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-full bg-violet-600 text-white text-[10px] font-bold tracking-wide">LIMITED</span>
          ) : null}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        {colors.length ? (
          <div className="mt-3 flex items-center gap-1.5">
            {colors.map((hex) => (
              <span key={hex} className="w-3.5 h-3.5 rounded-full border border-stone-200" style={{ background: hex }} />
            ))}
          </div>
        ) : null}
        <div className="pt-2 flex flex-col flex-1">
          <Link to={`${base}/product/${p.slug}`} className="font-medium text-stone-900 text-sm line-clamp-1">{p.name}</Link>
          <div className="mt-1 flex items-center flex-wrap gap-1.5">
            <span className={`text-sm font-semibold font-mono-nums ${p.mrp && p.mrp > p.price ? "text-red-600" : "text-stone-900"}`}>{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span> : null}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "quicksto") {
    return (
      <div className="group flex flex-col text-left h-full bg-white" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square rounded-xl overflow-hidden bg-[#F7F8FA] border border-stone-100">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="pt-3 flex flex-col flex-1">
          <Link to={`${base}/product/${p.slug}`} className="font-bold text-stone-900 text-sm line-clamp-2 leading-snug min-h-[2.5rem]">{p.name}</Link>
          {p.mrp && p.mrp > p.price ? (
            <div className="mt-1.5 text-xs text-stone-400 font-mono-nums">MRP {money(p.mrp, p.currency_symbol || "₹")}</div>
          ) : (
            <div className="mt-1.5 text-xs text-stone-400 font-mono-nums">MRP {money(p.price, p.currency_symbol || "₹")}</div>
          )}
          <div className="mt-0.5 flex items-center flex-wrap gap-1.5">
            <span className={`text-base font-extrabold font-mono-nums ${p.mrp && p.mrp > p.price ? "text-red-600" : "text-stone-900"}`}>{money(p.price, p.currency_symbol || "₹")}</span>
            {discount > 0 ? <span className="text-xs font-bold text-red-600">({discount}%)</span> : null}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "carparts" || variant === "carpartsDeal") {
    const cat = p.category_name || p.category || "";
    const deal = variant === "carpartsDeal";
    return (
      <div className={`group flex flex-col text-left h-full bg-white ${deal ? "rounded-2xl border-2 overflow-hidden" : "border border-stone-100 rounded-xl overflow-hidden"}`} style={deal ? { borderColor: "var(--store-primary)" } : undefined} data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square overflow-hidden bg-white">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
          )}
          {deal && discount > 0 ? (
            <span className="absolute left-2 top-2 px-2 py-1 rounded-md text-[11px] font-extrabold text-white" style={{ background: "var(--store-primary)" }} data-testid={`sale-tag-${p.slug}`}>{discount}% OFF</span>
          ) : null}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="px-3 pb-3 pt-3 flex flex-col flex-1">
          {cat ? <div className="text-[11px] text-stone-400 truncate">{cat}</div> : null}
          <Link to={`${base}/product/${p.slug}`} className="mt-0.5 font-bold text-stone-900 text-sm line-clamp-2 leading-snug min-h-[2.5rem]">{p.name}</Link>
          <div className="mt-1.5 flex items-center flex-wrap gap-1.5">
            <span className="text-lg font-extrabold font-mono-nums" style={{ color: "var(--store-primary)" }}>{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span> : null}
          </div>
          <div className="mt-2 flex items-center gap-1">
            <Stars value={rating || 0} />
            <span className="text-xs text-stone-400">({ratingCount})</span>
          </div>
          <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
            className="mt-4 w-full h-11 rounded-lg text-[11px] font-extrabold tracking-[0.14em] uppercase text-white disabled:opacity-50 inline-flex items-center justify-center gap-2 hover:opacity-90 transition-opacity" style={{ background: "var(--store-secondary)" }}>
            {addLabel}
            <ShoppingCart className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  if (variant === "furnis") {
    const kicker = p.brand || p.vendor || p.vendor_name || "";
    return (
      <div className="group flex flex-col text-left h-full bg-white" data-testid={`product-card-${p.slug}`}>
        <Link to={`${base}/product/${p.slug}`} className="relative aspect-square overflow-hidden bg-white">
          {cover ? (
            <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl italic">{p.name.slice(0, 1)}</div>
          )}
          <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
        </Link>
        <div className="pt-4 flex flex-col flex-1">
          {kicker ? <div className="furnis-sans text-[11px] tracking-[0.04em] text-stone-400">{kicker}</div> : null}
          <Link to={`${base}/product/${p.slug}`} className="font-display italic text-lg sm:text-xl text-stone-800 leading-snug line-clamp-2">{p.name}</Link>
          <div className="mt-1.5 flex items-center flex-wrap gap-1.5 font-mono-nums">
            <span className="furnis-sans text-sm text-stone-800">{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? <span className="furnis-sans text-xs text-stone-400 line-through">{money(p.mrp, p.currency_symbol || "₹")}</span> : null}
          </div>
        </div>
      </div>
    );
  }

  if (variant === "ondemand") {
    const unit = (p.short_description || p.sku || p.unit || "").toString().replace(/<[^>]+>/g, " ").trim().slice(0, 48);
    const offAmt = p.mrp && p.mrp > p.price ? Math.round(p.mrp - p.price) : 0;
    return (
      <div className="group flex flex-col text-left h-full bg-white" data-testid={`product-card-${p.slug}`}>
        <div className="relative">
          <Link to={`${base}/product/${p.slug}`} className="relative block aspect-square rounded-2xl overflow-hidden bg-[#F4F6FB] border border-stone-100">
            {cover ? (
              <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
            )}
            <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
          </Link>
          {offAmt > 0 ? (
            <span className="absolute left-2 top-2 px-1.5 py-0.5 rounded-md text-[10px] font-extrabold text-white" style={{ background: "var(--store-primary, #F43F5E)" }} data-testid={`sale-tag-${p.slug}`}>₹{offAmt} OFF</span>
          ) : null}
          <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
            className="ondemand-add absolute bottom-2 right-2 z-10 h-9 px-4 rounded-xl text-[12px] font-extrabold uppercase tracking-wide text-white disabled:opacity-50 inline-flex items-center justify-center leading-none"
            style={{ background: "var(--store-primary, #F43F5E)" }}>
            ADD
          </button>
        </div>
        <div className="pt-2 px-0.5 flex flex-col flex-1">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            <span className="text-base font-extrabold text-stone-900 font-mono-nums">{money(p.price, p.currency_symbol || "₹")}</span>
            {p.mrp && p.mrp > p.price ? <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span> : null}
          </div>
          {offAmt > 0 ? <div className="mt-0.5 text-[11px] font-bold text-rose-600 font-mono-nums">₹{offAmt} OFF</div> : null}
          <div className="mt-2 border-t border-dashed border-stone-200" />
          <Link to={`${base}/product/${p.slug}`} className="mt-2 font-semibold text-stone-800 text-xs sm:text-sm line-clamp-2 leading-snug">{p.name}</Link>
          {unit ? <div className="mt-0.5 text-[11px] text-stone-400 line-clamp-1">{unit}</div> : null}
          {ratingCount > 0 ? (
            <div className="mt-1 flex items-center gap-1 text-[11px] text-stone-600" data-testid={`rating-${p.slug}`}>
              <Star className="w-3.5 h-3.5 text-emerald-600" fill="#059669" />
              <span className="font-semibold font-mono-nums">{rating.toFixed(1)}</span>
              <span className="text-stone-400 font-mono-nums">({ratingCount >= 1000 ? `${(ratingCount / 1000).toFixed(1)}k` : ratingCount})</span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <Link to={`${base}/product/${p.slug}`} className="group relative flex flex-col rounded-2xl bg-white border border-stone-200/70 overflow-hidden hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.10)] transition-shadow" data-testid={`product-card-${p.slug}`}>
      <div className="relative aspect-square bg-stone-100 overflow-hidden">
        {cover ? (
          <img src={mediaUrl(cover)} alt={p.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-300 font-display text-3xl">{p.name.slice(0, 1)}</div>
        )}
        {/* Sale tag */}
        {discount > 0 && (
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[11px] font-bold text-white bg-rose-600 shadow-sm" data-testid={`sale-tag-${p.slug}`}>Sale</div>
        )}
        {/* Wishlist heart */}
        <button type="button" onClick={handleWish} aria-label="Toggle wishlist" data-testid={`wish-btn-${p.slug}`}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm hover:scale-110 transition-transform">
          <Heart className={`w-4 h-4 ${saved ? "text-rose-600" : "text-stone-500"}`} fill={saved ? "#e11d48" : "none"} />
        </button>
        <OverlayNote paused={paused} pausedMsg={pausedMsg} outOfStock={outOfStock} testId={`store-offline-${p.slug}`} />
      </div>

      <div className="p-3 flex flex-col flex-1">
        <div className="font-medium text-stone-900 line-clamp-1">{p.name}</div>
        {p.is_digital && <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700">Digital</div>}
        {/* Rating row */}
        {ratingCount > 0 && (
          <div className="mt-1 flex items-center gap-1.5" data-testid={`rating-${p.slug}`}>
            <Stars value={rating} />
            <span className="text-[11px] text-stone-500 font-mono-nums">{rating.toFixed(1)} · {ratingCount}</span>
          </div>
        )}
        <div className="mt-1 flex items-center gap-2">
          <span className="font-mono-nums font-bold text-stone-900">{money(p.price, p.currency_symbol || "₹")}</span>
          {p.mrp && p.mrp > p.price && (
            <>
              <span className="text-xs text-stone-400 line-through font-mono-nums">{money(p.mrp, p.currency_symbol || "₹")}</span>
              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 px-1.5 rounded">-{discount}%</span>
            </>
          )}
        </div>
        <button type="button" onClick={handleAdd} disabled={outOfStock || paused} data-testid={`add-cart-${p.slug}`}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-full text-sm font-semibold text-white btn-store-primary disabled:opacity-50 disabled:cursor-not-allowed transition-opacity">
          <ShoppingCart className="w-4 h-4" /> {addLabel}
        </button>
      </div>
    </Link>
  );
}
